import { TimerInstance } from "@amzn/xb-hooks";
import { LiveComponentSource, LiveComponentSourceProps, LiveContext, VendedLiveSource } from ".";
import { ExperienceProviderConfig } from "../experience-providers";
import { getInterceptorFactories } from "../interceptors";
import { LocalizationProvider } from "../localization";
import { createScriptElement, retryWithExponentialBackoff } from "./federationUtils";
import { RemoteComponentInfo } from "./RemoteComponent";
import { useContext } from "react";
import { RemoteSideErrorBoundary } from "./RemoteSideErrorBoundary";

/* Webpack Types */
type Scope = unknown;

/* Factory function that returns the requested module from the container */
type Factory = () => any;

/* Weback default variable that initializes the shared scope and adds all known provided modules from local build or remote container build */
declare const __webpack_init_sharing__: (shareScope: string) => Promise<void>;

/* Another default Webpack variable, which initializes the exposed module or the container */
declare const __webpack_share_scopes__: { default: Scope };

/* Webpack container */
export interface Container {
  init(shareScope: Scope): void;
  get(module: string): Promise<Factory>;
}

export interface ContainerLoadingMetrics {
  loadRemoteComponentTimer: TimerInstance;
  initRemoteComponentTimer: TimerInstance;
  recordLoadSuccess: () => void;
  recordLoadFailure: () => void;
  recordInitSuccess: () => void;
  recordInitFailure: () => void;
}

/**
 * Get's the remote's code and appends it to the body of the document. The promise resolves
 * when the remote script is successfully loaded on the body of the application.
 */
export const injectRemoteIntoDOMAndFetch = (
  url: string,
  remoteName: string,
  metrics: ContainerLoadingMetrics
): Promise<Container> =>
  new Promise((resolve, reject) => {
    // We attach a script element to the DOM so the browser can fetch the remoteEntry.js file
    const scriptElement = createScriptElement(url);

    scriptElement.onerror = (err) => {
      console.error(err);
      metrics.recordInitFailure();
      reject(
        new Error(
          `Failed to inject ${remoteName} script into DOM. Please validate the XPS URL is correct and your application can access it.`
        )
      );
    };

    // When the script is loaded we need to resolve the promise back to Module Federation
    scriptElement.onload = () => {
      // The script is now loaded on window using the name defined within the remote
      const proxy = {
        get: (request: Request) => (window as any)[remoteName].get(request),
        init: (arg: any) => {
          try {
            const container = (window as any)[remoteName].init(arg);
            return container;
          } catch (err) {
            console.error(err);
            console.error(`Failed to initialize remote: ${remoteName}. ${err}`);
            metrics.recordInitFailure();
            reject(err);
          }
        },
      };
      console.log(`Successfully initialized remote: ${remoteName}.`);
      resolve(proxy as unknown as Container);
    };

    // Lastly we inject the script tag into the document's head to trigger the script load
    document.head.appendChild(scriptElement);
  });

/**
 * Initialized correct scopes for webpack, attempts to retrieve remote from window, then returns
 * the module. (Console timing function for now - but this could be tracked on a CW metric.)
 */
export const loadRemoteComponent =
  (
    { endpoint, experienceProviderName, componentName, fallback }: RemoteComponentInfo,
    metrics: ContainerLoadingMetrics
  ) =>
  async () => {
    console.time(`LoadingRemoteContainer: [${experienceProviderName}]`);
    if (!(experienceProviderName in window)) {
      const publishRemoteComponentInitTime = metrics.initRemoteComponentTimer.start();
      await __webpack_init_sharing__("default");
      const fetchedContainer = await retryWithExponentialBackoff(
        injectRemoteIntoDOMAndFetch(
          `${endpoint.replace(/\/$/, "")}/_next/static/chunks/remoteEntry.js`,
          experienceProviderName,
          metrics
        ),
        2,
        500
      );
      fetchedContainer.init(__webpack_share_scopes__["default"]);
      publishRemoteComponentInitTime();
      metrics.recordInitSuccess();
    }

    // Remote is loaded in window
    let container;
    let Module;
    try {
      const publishRemoteComponentLoadTime = metrics.loadRemoteComponentTimer.start();
      container = (window as any)[experienceProviderName];
      const factory = await container.get(`./${componentName}`);
      Module = factory();
      publishRemoteComponentLoadTime();
      metrics.recordLoadSuccess();
      console.timeEnd(`LoadingRemoteContainer: [${experienceProviderName}]`);
    } catch (err) {
      metrics.recordLoadFailure();
      throw err;
    }

    const {
      __interceptorChain = [],
      __errorFallbackUI,
      __additionalProps,
      __lcType,
    } = (Module.default as VendedLiveSource) ?? {};

    const localizationProps = {
      arbManifest: (await container.get("/arbManifest.js"))().default,
      resolveResourcePack: async (url?: string) => (await container.get(url))(),
    };
    // Create Platform provided interceptors
    const { earlyInterceptors, lateInterceptors } = getInterceptorFactories(__lcType);
    const interceptorFactories = [...lateInterceptors, ...__interceptorChain, ...earlyInterceptors];

    // TODO - add unit tests https://tasks.awstcprojects.com/browse/PLATFORM-6249
    /**
     * Create the component that will actually be called each time. Everything else
     * in the load function (including the factory calls) happens only once for every
     * endpoint-xpsName-componentName combination. This is important to realize so we don't
     * accidentally "cache" something that should be ran every time a component is called.
     *
     * This is why we only pass the RemoteComponentInfo to the interceptor factories as that is
     * all that is guaranteed not to go stale.
     */
    const Component: LiveComponentSource = interceptorFactories.reduce(
      (liveResp, factory) => factory({ experienceProviderName, componentName, fallback }, liveResp),
      Module.default
    );

    return {
      // This Component + the Interceptors is ran every time the component is called
      default: (props: LiveComponentSourceProps) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const currentLiveContextVals = useContext(LiveContext);
        return (
          // FIXME: Move LocalizationProvider to an interceptor
          <LiveContext.Provider
            value={{
              ...currentLiveContextVals,
              lcType: __lcType,
              remoteProvidedErrorFallback: __errorFallbackUI,
            }}
          >
            <RemoteSideErrorBoundary xpsName={experienceProviderName} componentName={componentName}>
              <LocalizationProvider {...localizationProps}>
                <Component {...props} {...__additionalProps} />
              </LocalizationProvider>
            </RemoteSideErrorBoundary>
          </LiveContext.Provider>
        );
      },
    };
  };

export const findXpsComponentEndpoint = (remoteName: string, remotes: ExperienceProviderConfig[]): string => {
  const remote = remotes?.find((r) => r.name === remoteName);
  return remote?.componentEndpoint || "";
};
